import { useState } from 'react';
import { guardLevel, guardRecover, guardStateSet, guardStatus, settingsAlways, type ApiError } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, btn, chip, sx, useApi, useGuarded, usePage } from './kit';
import { RuleRows } from './RuleRows';
import { ruleRows } from './RetentionSection';
import { useCustomerText } from '../../ui/buildLabels';
import { BuildingLoading, PickOneBuilding, useBuilding } from './building';

type GState = 'safe' | 'degraded' | 'halted';
const GSTATES: [GState, string, string, string, string][] = [
  ['safe', 'Safe', 'Everything enforcing normally.', '#1F6B52', 'rgba(47,138,111,.16)'],
  ['degraded', 'Degraded', 'Enforcing, but a dependency is unhealthy. Riskier actions hold for review.', '#5B6B7C', 'rgba(91,107,124,.18)'],
  ['halted', 'Halted', 'The gate stops all model-initiated action platform-wide. Humans still work.', '#B3261E', 'rgba(179,38,30,.14)'],
];
const LEVELS: [string, string, string, string, string][] = [
  ['L0', 'Observe', 'Log only. Nothing is blocked, nothing is paged.', '#54606C', 'rgba(34,39,46,.08)'],
  ['L1', 'Notify', 'Log and notify the on-call channel. Still nothing blocked.', '#264C7E', 'rgba(79,138,214,.16)'],
  ['L2', 'Hold', 'Risky actions wait for a human to approve them.', '#264C7E', 'rgba(79,138,214,.16)'],
  ['L3', 'Restrict', 'Autonomy drops to read-only. Outbound resident contact pauses.', '#5B6B7C', 'rgba(91,107,124,.18)'],
  ['L4', 'Freeze', 'All model-initiated action stops. Existing sessions end cleanly.', '#B3261E', 'rgba(179,38,30,.14)'],
  ['L5', 'Lockdown', 'Freeze plus key rotation and a mandatory incident review before resume.', '#B3261E', 'rgba(179,38,30,.14)'],
];
const stateOf = (s: string | undefined): GState => (s === 'HALTED' ? 'halted' : s && s.startsWith('DEGRADED') ? 'degraded' : 'safe');

/**
 * Guard & Response (design seIsGuardResp, T9811; GSTATES, LEVELS L21962-21979 and L22818-22844).
 * One of the building sections the facility switcher gates (V6 README): with a region or
 * several buildings picked it asks to pick one first. The posture itself is the whole
 * platform's (the server has one guard), and with one building picked the section says so.
 */
export function GuardSection() {
  const building = useBuilding();
  if (building.mode === 'loading') return <BuildingLoading />;
  if (building.mode === 'narrowed') return <PickOneBuilding what="Guard and Response" />;
  return <Guard picked={building.mode === 'picked' ? building.name : null} />;
}

function Guard({ picked }: { picked: string | null }) {
  const plain = useCustomerText();
  const { client } = useSession();
  const { ask, changed } = usePage();
  const { canWrite, roReason, wr } = useGuarded();
  const g = useApi((c, s) => guardStatus(c, { signal: s }), []);
  const always = useApi((c, s) => settingsAlways(c, { signal: s }), []);
  const [sel, setSel] = useState<number | null>(null);
  const [pushedAt, setPushedAt] = useState<{ lvl: number; state: GState } | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);

  if (!g.data && !g.error) return <Skeleton rows={6} label="Loading the guard" />;
  const gState = stateOf(g.data?.state);
  const live = g.data?.responseLevel ?? 0;
  const lvlIdx = sel ?? live;
  const lvl = LEVELS[Math.max(0, Math.min(5, lvlIdx))];
  const pushed = !!pushedAt && pushedAt.lvl === lvlIdx && pushedAt.state === gState;
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const setState = (s: GState) => {
    if (s === gState) return;
    if (s === 'halted') {
      ask({
        sec: 'Guard & Response', title: 'Halt all model actions?', body: 'Every model-initiated action stops platform-wide. People keep working; residents can’t talk to Atlas until you resume.', btn: 'Halt',
        go: async () => { await guardStateSet(client, { body: { state: 'HALTED', confirm: true } }); g.reload(); },
      });
      return;
    }
    void run(async () => {
      if (s === 'safe') await guardRecover(client, { body: {} });
      else await guardStateSet(client, { body: { state: 'DEGRADED_READ_ONLY', confirm: true } });
      g.reload();
    });
  };

  return (
    <div data-screen-label="Guard and Response" data-testid="settings-guard" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;max-width:900px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;')} />
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Guard &amp; Response</span>
        <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
        <span data-testid="guard-level-now" style={sx(chip(lvl[3], lvl[4]))}>{I(lvl[0] + ' · ' + lvl[1])}</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:16px;text-wrap:pretty;')}>
        {I((picked ? 'One global posture for the whole platform, not only ' + picked + '.' : 'One global posture for the whole platform.') + ' Per-guard thresholds and warn / hold / block actions stay in Policy - this dial does not duplicate them, it overrides them downward.')}
      </div>
      {g.error && <ErrLine error={g.error} style="margin:-8px 0 12px;" />}
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:9px;')}>Guard state</div>
      <div role="radiogroup" aria-label="Guard state" style={sx('display:flex;align-items:stretch;gap:9px;flex-wrap:wrap;row-gap:9px;')}>
        {GSTATES.map((s) => (
          <a
            key={s[0]}
            href="#"
            role="radio"
            aria-checked={gState === s[0]}
            {...wr(() => setState(s[0]))}
            style={sx('box-sizing:border-box;flex:1 1 190px;min-width:min(100%,190px);display:block;padding:12px 14px;border-radius:10px;text-decoration:none;color:#22272E;' + (gState === s[0] ? 'background:' + s[4] + ';border:1px solid ' + s[3] + ';' : 'background:#F7F3EA;border:1px solid rgba(34,39,46,.13);'))}
          >
            <span style={sx('display:block;font-size:16px;font-weight:700;color:' + (gState === s[0] ? s[3] : '#22272E') + ';')}>{I(s[1])}</span>
            {' '}
            <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:4px;line-height:1.45;')}>{I(s[2])}</span>
          </a>
        ))}
      </div>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:16px 0 9px;')}>Response level</div>
      <div role="radiogroup" aria-label="Response level">
        {LEVELS.map((l, i) => (
          <a
            key={l[0]}
            href="#"
            role="radio"
            aria-checked={lvlIdx === i}
            aria-disabled={!canWrite || undefined}
            title={roReason ?? undefined}
            onClick={(e) => { e.preventDefault(); if (canWrite) setSel(i); }}
            style={sx('box-sizing:border-box;display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:11px 13px;border-radius:10px;text-decoration:none;color:#22272E;margin-bottom:6px;' + (lvlIdx === i ? 'background:' + l[4] + ';border:1px solid ' + l[3] + ';' : 'background:#F7F3EA;border:1px solid rgba(34,39,46,.11);'))}
          >
            <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:34px;height:26px;flex:0 0 34px;border-radius:6px;font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;white-space:nowrap;' + (lvlIdx === i ? 'background:' + l[3] + ';color:#fff;' : 'background:rgba(34,39,46,.07);color:#54606C;'))}>{I(l[0])}</span>
            <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')}>
              <span style={sx('display:block;font-size:16px;font-weight:700;')}>{I(l[1])}</span>
              <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:3px;line-height:1.45;')}>{I(l[2])}</span>
            </span>
            {lvlIdx === i && <span style={sx('font-size:16px;color:#2F5C97;font-weight:600;white-space:nowrap;')}>selected</span>}
          </a>
        ))}
      </div>
      <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:8px;margin-top:14px;padding-top:13px;border-top:1px solid rgba(34,39,46,.1);')}>
        <span style={sx('font-size:16px;color:' + (pushed ? '#1F6B52' : '#54606C') + ';text-wrap:pretty;')}>
          {I(pushed
            ? 'Enforcement posture is live at ' + lvl[0] + ' with guard state ' + gState + '. Every push is written to the Truth Ledger with the operator who made it.'
            : 'Nothing is pushed yet. The gate keeps enforcing at its current level until you push this one.')}
        </span>
        <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
        <a
          href="#"
          {...wr(() => void run(async () => {
            await guardLevel(client, { body: { level: lvlIdx, confirm: true } });
            setPushedAt({ lvl: lvlIdx, state: gState });
            g.reload();
          }))}
          style={sx(btn(!pushed))}
        >
          {I(pushed ? plain('Pushed to /api/guard/level') : 'Push to the gate')}
        </a>
      </div>
      <ErrLine error={err} />
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:10px 0 2px;')}>Rules that always apply</div>
      <RuleRows plain pad="5px 0" ro={roReason} rows={ruleRows(always.data?.guardResponseRows, always.data?.guardResponse)} />
      {always.error && <ErrLine error={always.error} />}
    </div>
  );
}
